import { useEffect, useState } from "react";
import type { SalonService } from "../../../../types/salon";
import type { ServiceRow } from "./bookingTypes";
import { DraftBlocker, DraftBlockerField, draftBlocker } from "./draftModel";

export interface NextBlocker {
  /** The reason being shown (only after Next was clicked), else null. */
  shown: DraftBlocker | null;
  /**
   * Call from Next: shows the current reason and returns it, or returns
   * null when the draft can advance.
   */
  reveal: () => DraftBlocker | null;
  /** Whether `field` (on row `uid`, null for the client) is flagged. */
  flags: (uid: number | null, field: DraftBlockerField) => boolean;
}

/**
 * Next stays clickable while a booking draft is incomplete: clicking it
 * surfaces why (the first missing field) instead of a silently disabled
 * button. Only the issue Next was clicked on is flagged: once it's resolved
 * the warning clears, so newly revealed fields (e.g. services after picking
 * a client) aren't marked as errors before the user has had a chance to
 * fill them. Shared by the desktop drawer and the mobile sheet.
 */
export const useNextBlocker = (
  rows: ServiceRow[],
  clientUuid: string,
  services: SalonService[] | null | undefined,
): NextBlocker => {
  const blocker = draftBlocker(rows, clientUuid, services);
  const blockerKey = blocker ? `${blocker.field}:${blocker.uid}` : null;
  const [shownKey, setShownKey] = useState<string | null>(null);

  useEffect(() => {
    if (shownKey && blockerKey !== shownKey) setShownKey(null);
  }, [blockerKey, shownKey]);

  const shown = blocker && blockerKey === shownKey ? blocker : null;

  return {
    shown,
    reveal: () => {
      setShownKey(blockerKey);
      return blocker;
    },
    flags: (uid, field) => shown?.uid === uid && shown.field === field,
  };
};
